import moment from 'moment';

import { appointmentStatus } from '../../../config/app.config';
import { formatDate } from '../../../helpers/formatDate';

export const APPOINTMENTS_HISTORY_SCOPE = {
  hereNow: 'hereNow',
  past: 'past',
};

export const APPOINTMENTS_SCOPE_OPTIONS = [
  { value: APPOINTMENTS_HISTORY_SCOPE.hereNow, label: 'Here now' },
  { value: APPOINTMENTS_HISTORY_SCOPE.past, label: 'Past' },
];

export const APPOINTMENTS_SORT_OPTIONS = [
  { value: 'dateTime', label: 'Date', defaultOrder: -1 },
  { value: 'parentName', label: 'Parent', defaultOrder: 1 },
  { value: 'petName', label: 'Pet', defaultOrder: 1 },
];

export const PROFILE_APPOINTMENTS_SORT_OPTIONS = [
  { value: 'dateTime', label: 'Date', defaultOrder: -1 },
  { value: 'petName', label: 'Pet', defaultOrder: 1 },
];

/**
 * Profile Update History Sort control.
 * - All Pets: Date + Pet
 * - Individual pet: Date only
 * - Date defaultOrder follows view: history newest first (-1), Future earliest first (1)
 */
export function getProfileAppointmentsSortOptions({
  futureView = false,
  petId = null,
} = {}) {
  const dateOption = {
    value: 'dateTime',
    label: 'Date',
    defaultOrder: futureView ? 1 : -1,
  };

  if (petId) {
    return [dateOption];
  }

  return [dateOption, PROFILE_APPOINTMENTS_SORT_OPTIONS[1]];
}

export const APPOINTMENTS_PAST_DATE_SHORTCUTS = [
  { value: 'today', label: 'Today' },
  { value: 'last 7 days', label: '7d' },
  { value: 'last 30 days', label: '30d' },
];

export const APPOINTMENTS_FUTURE_DATE_SHORTCUTS = [
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: 'next 7 days', label: '7d' },
  { value: 'next 30 days', label: '30d' },
];

export const APPOINTMENTS_FILTER_SEARCH_THRESHOLD = 8;

const STATUS_CHOICES = [
  { value: appointmentStatus.upcoming, label: 'Upcoming' },
  { value: appointmentStatus.ongoing, label: 'Ongoing' },
  { value: appointmentStatus.past, label: 'Past' },
  { value: appointmentStatus.no_updates, label: 'No Updates' },
  { value: appointmentStatus.cancelled, label: 'Cancelled' },
  { value: appointmentStatus.expired, label: 'Expired' },
];

export function getAppointmentsDateShortcuts(isFutureView) {
  return isFutureView
    ? APPOINTMENTS_FUTURE_DATE_SHORTCUTS
    : APPOINTMENTS_PAST_DATE_SHORTCUTS;
}

function facilityToday(utcOffset = 0) {
  return moment().utc().add(utcOffset, 'minutes').startOf('date');
}

export function buildAppointmentsTimeFilter(shortcutValue, utcOffset = 0) {
  if (!shortcutValue) {
    return null;
  }

  if (
    shortcutValue === 'today' ||
    shortcutValue === 'last 7 days' ||
    shortcutValue === 'last 30 days'
  ) {
    return { type: shortcutValue };
  }

  const tomorrow = facilityToday(utcOffset).add(1, 'day');

  if (shortcutValue === 'tomorrow') {
    const date = formatDate(tomorrow);
    return { type: 'day', startDate: date, endDate: date };
  }

  if (shortcutValue === 'next 7 days') {
    return {
      type: 'range',
      startDate: formatDate(tomorrow),
      endDate: formatDate(tomorrow.clone().add(6, 'days')),
    };
  }

  if (shortcutValue === 'next 30 days') {
    return {
      type: 'range',
      startDate: formatDate(tomorrow),
      endDate: formatDate(tomorrow.clone().add(29, 'days')),
    };
  }

  return { type: shortcutValue };
}

export function getAppointmentsFilterCategories({
  appointmentTypes = [],
  doctors = [],
  appointmentTypeLabel = 'Appointment Type',
}) {
  const typeChoices = appointmentTypes.map((appointmentType) => ({
    value: appointmentType._id,
    label: appointmentType.title,
  }));

  const doctorChoices = doctors.map((doctor) => ({
    value: doctor._id,
    label: doctor.name,
  }));
  doctorChoices.push({ value: 'NA', label: 'N/A' });

  return [
    {
      key: 'status',
      label: 'Status',
      choices: STATUS_CHOICES,
    },
    {
      key: 'typeTitle',
      label: appointmentTypeLabel,
      choices: typeChoices,
    },
    {
      key: 'doctor',
      label: 'Doctor',
      choices: doctorChoices,
    },
  ];
}
